import { WarningTwoIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  HStack,
} from "@chakra-ui/react";
import { WaterfallPreferredReturnStats } from "features/waterfall/review/WaterfallPreferredReturnStats";
import { useExternalIDsRetrieveSuspense } from "gen/endpoints/external-id/external-id";
import { useHypotheticalWaterfallPreferredReturnSummaryRetrieveSuspense } from "gen/endpoints/financial-reporting/financial-reporting";
import { useGlobalEntityRetrieveSuspense } from "gen/endpoints/global-entity/global-entity";
import { useInvestorEntityRetrieveSuspense } from "gen/endpoints/investor-entity/investor-entity";
import { GlobalEntityData } from "gen/model/globalEntityData";
import { InvestorEntityData } from "gen/model/investorEntityData";
import React from "react";
import { useTranslation } from "react-i18next";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

const HypotheticalWaterfallPrefBreakdown: React.FC<{
  endOfPeriodReportId: string;
  fundFamilyId: string;
  hypotheticalWaterfallId: string;
  investorEntityId?: string | undefined;
  fundEntityId: string;
  entityData?: InvestorEntityData | GlobalEntityData;
}> = ({
  endOfPeriodReportId,
  fundFamilyId,
  hypotheticalWaterfallId,
  investorEntityId,
  fundEntityId,
  entityData,
}) => {
  const { t } = useTranslation(["common", "distribution", "waterfall"]);

  const { data: summaryData } =
    useHypotheticalWaterfallPreferredReturnSummaryRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      {
        fund_id: fundEntityId,
        investor_entity_id: investorEntityId,
      },
    );

  const financialReporingRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({});

  const { data: externalIdList } = useExternalIDsRetrieveSuspense(
    investorEntityId!,
  );

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        title={t("waterfall:preferredReturnReview.title")}
        backButton={{
          title: investorEntityId
            ? t("waterfall:preferredReturnReview.investorLevel.back")
            : t("waterfall:preferredReturnReview.fundLevel.back"),
          path: investorEntityId
            ? financialReporingRoute
                .hypotheticalWaterfall({
                  hypotheticalWaterfallId,
                  endOfPeriodReportId,
                })
                .investorWaterfallDetails({
                  fundEntityId,
                  investorEntityId,
                }).$
            : financialReporingRoute
                .hypotheticalWaterfall({
                  hypotheticalWaterfallId,
                  endOfPeriodReportId,
                })
                .review({})
                .waterfallDetails({}).$,
        }}
        badge={
          <HStack spacing="8px">
            {entityData ? (
              <Badge key={entityData.id}> {entityData.legal_name}</Badge>
            ) : undefined}
            {externalIdList.map((externalID) => (
              <Badge key={externalID.id}>{externalID.name}</Badge>
            ))}
          </HStack>
        }
        stats={
          <WaterfallPreferredReturnStats data={summaryData.pref_details} />
        }
      />
      <PageBody>
        {summaryData.pref_details.show_pref_discrepancy_banner && (
          <Alert
            status="warning"
            mb="24px"
            data-ls-enabled={true}
            data-public={true}
          >
            <AlertIcon>
              <WarningTwoIcon />
            </AlertIcon>
            <AlertDescription>
              {t("financialReporting:preferredReturnBreakdown.alert")}
            </AlertDescription>
          </Alert>
        )}
        <TableBox isFullWidth={true}>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("waterfall:preferredReturnReview.tableTitle")}
            </TableBoxTitle>
            <DataTableBoxActions />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={summaryData.table_data} />
          </TableBoxBody>
        </TableBox>
      </PageBody>
    </PageContent>
  );
};

export const HypotheticalWaterfallPrefFundBreakdown: React.FC = () => {
  const {
    fundFamilyId,
    endOfPeriodReportId,
    hypotheticalWaterfallId,
    fundEntityId,
  } = useRequiredStringParams();

  const { data: entityData } = useGlobalEntityRetrieveSuspense(fundEntityId);

  return (
    <HypotheticalWaterfallPrefBreakdown
      endOfPeriodReportId={endOfPeriodReportId}
      fundFamilyId={fundFamilyId}
      hypotheticalWaterfallId={hypotheticalWaterfallId}
      fundEntityId={fundEntityId}
      entityData={entityData}
    />
  );
};

export const HypotheticalWaterfallPrefInvestorBreakdown: React.FC = () => {
  const {
    fundFamilyId,
    endOfPeriodReportId,
    hypotheticalWaterfallId,
    fundEntityId,
    investorEntityId,
  } = useRequiredStringParams();

  const { data: entityData } =
    useInvestorEntityRetrieveSuspense(investorEntityId);

  return (
    <HypotheticalWaterfallPrefBreakdown
      endOfPeriodReportId={endOfPeriodReportId}
      fundFamilyId={fundFamilyId}
      hypotheticalWaterfallId={hypotheticalWaterfallId}
      fundEntityId={fundEntityId}
      investorEntityId={investorEntityId}
      entityData={entityData}
    />
  );
};
